import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { AdminLink } from '../adminNav';
import { Btn, DangerBtn, Loading, Msg, Section, fmtDate, panelPost, useAdminGet } from '../kit';
import {
  COVER_EXTS, COVER_MAX_MB, Pill, S, SAMPLE_COVER_PATH, SAMPLE_PDF_MAX_MB, SAMPLE_PDF_PATH, fmtBytes, list, num,
  postMultipart, rowsOf, saveBlob, str,
} from './shared';

/**
 * Samples — the four sample reports offered on /ip-intel-report, editable
 * here with no deploy: the card text (title, organisation line, detail), the
 * name the PDF downloads under, the cover image, the PDF itself, whether the
 * sample is shown and in what order. Next to each: how often it was ticked /
 * asked for / downloaded (GET ip_intel/samples).
 *
 * The built-in copy (ip_intel.SAMPLES + the files shipped in the deploy) is
 * the default; an edit is an override on top of it, per field. "Reset to
 * default" drops that one override (POST ip_intel/sample_update {reset}); an
 * uploaded file stays on the server either way, the page just stops using it.
 * Uploads go to the raw multipart routes in shared.tsx (admin bearer token).
 *
 * Above the cards, "How visitors receive samples" (GET / POST
 * ip_intel/delivery_settings[_save], app_settings ip_intel_delivery):
 * download on the page, e-mail as attachments, or both — the server
 * enforces it — plus the attachment budget, link expiry and send caps, and
 * whether the finished paid report is also attached to its delivery mail.
 */

type TextField = 'title' | 'org' | 'detail' | 'industry' | 'filename';
const TEXT_FIELDS: TextField[] = ['title', 'org', 'detail', 'industry', 'filename'];
const FIELD_LABEL: Record<TextField, string> = {
  title: 'Title', org: 'Organisation / patent line (not shown on the card)', detail: 'Detail (no longer shown on the card)',
  industry: 'Industry tag (shown on the card under the title)', filename: 'Download file name',
};

export type SampleDefaults = { title: string; org: string; detail: string; industry: string; filename: string; cover_url: string };

export type Sample = {
  id: string; title: string; org?: string; detail?: string; industry?: string; cover_url?: string; filename?: string;
  downloads?: number; leads?: number; links?: number; selections?: number; last_downloaded_at?: string;
  /** Sample mails that carried this report (attached or linked). */
  emailed?: number;
  file_present?: boolean; size_bytes?: number;
  /** Editable-samples fields (absent on an older server: the tab then reads as built-in copy). */
  defaults: SampleDefaults;
  overridden: string[];
  cover_uploaded: boolean; cover_updated_at: string;
  pdf_uploaded: boolean; pdf_updated_at: string; pdf_size_bytes?: number;
  /**
   * What page 1 of the PDF visitors receive is (ip_intel._delivered_pdf): card | uploaded | not_a4 |
   * pdf_newer | own | failed | missing — and that file's size. Absent on an older server.
   */
  pdf_cover?: string; delivered_size_bytes?: number;
  active: boolean; sort?: number;
  updated_at: string; updated_by: string;
  /** An admin download path for the current PDF, when the server offers one. */
  pdf_download_path: string;
};

const bool = (v: any, dflt: boolean) =>
  v === undefined || v === null || v === '' ? dflt : v === true || v === 1 || v === 'true' || v === '1';

export const normSamples = (d: any): Sample[] =>
  rowsOf(d, 'samples').map((s: any) => {
    const title = str(s.title) || str(s.id);
    const dd = s.default ?? s.defaults ?? {};
    return {
      id: str(s.id), title, org: str(s.org), detail: str(s.detail), industry: str(s.industry),
      cover_url: str(s.cover_url), filename: str(s.filename),
      downloads: s.downloads ?? s.download_count, leads: s.leads ?? s.lead_count, links: s.links, emailed: s.emailed,
      selections: s.selections ?? s.toggles, last_downloaded_at: str(s.last_download_at ?? s.last_downloaded_at),
      file_present: typeof s.file_present === 'boolean' ? s.file_present : undefined,
      size_bytes: s.size_bytes,
      defaults: {
        title: str(dd.title ?? title), org: str(dd.org ?? s.org), detail: str(dd.detail ?? s.detail),
        industry: str(dd.industry ?? s.industry),
        filename: str(dd.filename ?? s.filename), cover_url: str(dd.cover_url ?? s.cover_url),
      },
      overridden: list(s.overridden),
      cover_uploaded: bool(s.cover_uploaded, false), cover_updated_at: str(s.cover_updated_at),
      pdf_uploaded: bool(s.pdf_uploaded, false), pdf_updated_at: str(s.pdf_updated_at),
      pdf_size_bytes: s.pdf_size_bytes ?? undefined,
      pdf_cover: str(s.pdf_cover) || undefined,
      delivered_size_bytes: s.delivered_size_bytes ?? undefined,
      active: bool(s.active, true),
      sort: s.sort === undefined || s.sort === null || s.sort === '' ? undefined : num(s.sort),
      updated_at: str(s.updated_at), updated_by: str(s.updated_by),
      pdf_download_path: str(s.pdf_download_path ?? s.pdf_admin_path),
    };
  }).filter(s => s.id);

// ---------------------------------------------------------------------------
// Client-side checks — a courtesy; the server re-checks everything.
// ---------------------------------------------------------------------------
const LIMITS: Record<TextField, { min: number; max: number }> = {
  title: { min: 3, max: 200 }, org: { min: 0, max: 200 }, detail: { min: 0, max: 600 }, industry: { min: 0, max: 80 },
  filename: { min: 5, max: 200 },
};

const fieldProblem = (k: TextField, v: string): string => {
  const t = v.trim();
  const { min, max } = LIMITS[k];
  if (k === 'title' && t.length < min) return `The title needs at least ${min} characters.`;
  if (t.length > max) return `${FIELD_LABEL[k]} is too long (${t.length}/${max}).`;
  if (k === 'filename') {
    if (!/\.pdf$/i.test(t)) return 'The file name must end in .pdf.';
    // Mirrors ip_intel._SAMPLE_FILENAME_RE (the server has the final say).
    if (!/^[A-Za-z0-9][A-Za-z0-9._ ()-]{0,150}\.pdf$/i.test(t)) {
      return 'Start with a letter or digit; use only letters, digits, spaces, dots, dashes, underscores and brackets (at most 155 characters).';
    }
  }
  return '';
};

/** What the file really is, from its first bytes. '' when unreadable. */
const sniff = async (f: File): Promise<'png' | 'jpeg' | 'webp' | 'pdf' | 'other' | ''> => {
  try {
    const b = new Uint8Array(await f.slice(0, 12).arrayBuffer());
    if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'png';
    if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'jpeg';
    const asc = (i: number, n: number) => String.fromCharCode(...Array.from(b.slice(i, i + n)));
    if (asc(0, 4) === 'RIFF' && asc(8, 4) === 'WEBP') return 'webp';
    if (asc(0, 4) === '%PDF') return 'pdf';
    return 'other';
  } catch {
    return '';
  }
};

const uid = () => apiService.getStoredUserId() || '';

/** One line under the PDF: which first page the PDF visitors receive opens with. */
const PDF_COVER_NOTE: Record<string, { text: string; warn?: boolean }> = {
  card: { text: 'PDF cover: matches the card cover' },
  uploaded: { text: 'PDF cover: replaced by your uploaded cover' },
  not_a4: { text: 'Uploaded cover is landscape or square — the PDF keeps its own first page (use a portrait cover, e.g. 1240 × 1754)', warn: true },
  pdf_newer: { text: "PDF cover: the PDF's own first page (the PDF was uploaded after the cover)" },
  own: { text: "PDF cover: the uploaded PDF's own first page" },
  failed: { text: "Couldn't put the uploaded cover on the PDF — it keeps its own first page", warn: true },
};

type Note = { kind: 'ok' | 'error'; text: string } | null;
type Draft = Partial<Record<TextField | 'sort', string>>;

// ---------------------------------------------------------------------------
// One file slot (cover image or PDF): pick → check → upload with progress.
// ---------------------------------------------------------------------------
const Uploader: React.FC<{
  kind: 'cover' | 'pdf'; sample: Sample; busy: string; setBusy: (b: string) => void;
  onDone: (text: string) => void; onError: (text: string) => void;
}> = ({ kind, sample, busy, setBusy, onDone, onError }) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const isCover = kind === 'cover';
  const maxMb = isCover ? COVER_MAX_MB : SAMPLE_PDF_MAX_MB;
  const clear = () => { setFile(null); if (ref.current) ref.current.value = ''; };

  const pick = async (f: File | null) => {
    if (!f) { clear(); return; }
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    const okExt = isCover ? COVER_EXTS.includes(ext) : ext === 'pdf';
    if (!okExt) {
      onError(isCover ? `.${ext} is not accepted — use a PNG, JPG or WebP image.` : 'Upload a .pdf file.');
      clear(); return;
    }
    if (f.size > maxMb * 1048576) {
      onError(`That file is ${fmtBytes(f.size)}; the limit is ${maxMb} MB.`); clear(); return;
    }
    if (!f.size) { onError('That file is empty.'); clear(); return; }
    const real = await sniff(f);
    if (real && (isCover ? !['png', 'jpeg', 'webp'].includes(real) : real !== 'pdf')) {
      onError(isCover ? `${f.name} is not really a PNG, JPG or WebP image.` : `${f.name} is not a real PDF (it does not start with %PDF).`);
      clear(); return;
    }
    onError('');
    setFile(f);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(kind); setProgress(0);
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('caller_id', uid());
    const { error } = await postMultipart(isCover ? SAMPLE_COVER_PATH(sample.id) : SAMPLE_PDF_PATH(sample.id), form, setProgress);
    setBusy(''); setProgress(null);
    if (error) { onError(`Upload failed: ${error}`); return; }
    clear();
    onDone(isCover ? 'New cover image is live on the page.' : 'New PDF is live — the next sample download gets it.');
  };

  return (
    <div className="flex flex-col gap-1.5">
      <input ref={ref} type="file" className="hidden"
        accept={isCover ? 'image/png,image/jpeg,image/webp' : 'application/pdf,.pdf'}
        onChange={e => pick(e.target.files?.[0] || null)} />
      {!file ? (
        <Btn small onClick={() => ref.current?.click()} disabled={!!busy}
          title={isCover ? `Portrait PNG, JPG or WebP, up to ${COVER_MAX_MB} MB (A4 like 1240 × 1754 or 3:4; it also becomes page 1 of the PDF)` : `PDF, up to ${SAMPLE_PDF_MAX_MB} MB`}>
          {isCover ? 'Replace image…' : 'Replace PDF…'}
        </Btn>
      ) : (
        <div className="flex flex-col gap-1.5 rounded-lg border border-primary-300 bg-primary-50 p-2">
          <div className="text-xs text-grayscale-1300 break-all">
            {file.name} <span className="text-grayscale-1100">· {fmtBytes(file.size)}</span>
          </div>
          {progress !== null && (
            <div className="flex items-center gap-2 text-[11px] text-grayscale-1100">
              <div className="flex-1 bg-grayscale-200 rounded-full h-2 overflow-hidden">
                <div className="h-2 bg-primary-500 rounded-full" style={{ width: `${progress}%` }} />
              </div>
              {progress < 100 ? `${progress}%` : 'Processing…'}
            </div>
          )}
          <div className="flex gap-1.5 flex-wrap">
            <Btn small kind="primary" onClick={upload} disabled={!!busy}>
              {busy === kind ? 'Uploading…' : isCover ? 'Upload image' : 'Upload PDF'}
            </Btn>
            <Btn small onClick={clear} disabled={busy === kind}>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
const SampleCard: React.FC<{ s: Sample; position: number; maxDl: number; reload: () => void }> =
  ({ s, position, maxDl, reload }) => {
  const [draft, setDraft] = useState<Draft>({});
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState<Note>(null);

  // A clean card follows the server; a saved field drops out of the draft.
  const cur: Record<TextField | 'sort', string> = {
    title: s.title, org: str(s.org), detail: str(s.detail), industry: str(s.industry), filename: str(s.filename),
    sort: s.sort === undefined ? '' : String(s.sort),
  };
  const val = (k: TextField | 'sort') => draft[k] ?? cur[k];
  const changed = (Object.keys(draft) as (TextField | 'sort')[]).filter(k => (draft[k] ?? '').trim() !== cur[k].trim());
  const dirty = changed.length > 0;
  const problems = TEXT_FIELDS.map(k => [k, changed.includes(k) ? fieldProblem(k, val(k)) : ''] as const)
    .filter(([, p]) => p);
  // ip_intel.admin_sample_update takes 0–1000.
  const sortBad = changed.includes('sort') && !(/^\d{1,4}$/.test(val('sort').trim()) && Number(val('sort').trim()) <= 1000);
  const isOver = (k: string) => s.overridden.includes(k);

  useEffect(() => {
    if (msg?.kind !== 'ok') return;
    const t = setTimeout(() => setMsg(null), 6000);
    return () => clearTimeout(t);
  }, [msg]);

  const post = async (body: Record<string, any>, ok: string, busyKey: string) => {
    setBusy(busyKey); setMsg(null);
    const { error } = await panelPost(S, 'sample_update', { id: s.id, ...body });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return false; }
    setMsg({ kind: 'ok', text: ok });
    reload();
    return true;
  };

  const save = async () => {
    const body: Record<string, any> = {};
    const reset: string[] = [];
    for (const k of TEXT_FIELDS) {
      if (!changed.includes(k)) continue;
      const v = val(k).trim();
      // Typing the built-in text back is a reset, not an override that happens to match.
      if (v === s.defaults[k].trim() && isOver(k)) reset.push(k);
      else body[k] = v;
    }
    if (changed.includes('sort')) body.sort = Number(val('sort').trim());
    if (reset.length) body.reset = reset;
    const ok = await post(body, 'Saved — live on the page on its next load.', 'save');
    if (ok) setDraft({});
  };

  const resetField = async (k: TextField) => {
    const ok = await post({ reset: [k] }, `${FIELD_LABEL[k]} is back to the built-in text.`, `reset:${k}`);
    if (ok) setDraft(d => { const n = { ...d }; delete n[k]; return n; });
  };

  const dlPdf = async () => {
    const rel = s.pdf_download_path.replace(/^https?:\/\/[^/]+/i, '');
    const { blob, filename, error } = await apiService.adminFetchFile(uid(), rel);
    if (error || !blob) { setMsg({ kind: 'error', text: `Download failed: ${error || 'no file'}` }); return; }
    saveBlob(blob, filename || s.filename || `${s.id}.pdf`);
  };

  const input = (k: TextField, multiline = false) => {
    const v = val(k);
    const p = changed.includes(k) ? fieldProblem(k, v) : '';
    const cls = `w-full px-3 rounded-md border-[0.5px] bg-white text-sm text-grayscale-1300 ${
      p ? 'border-red-400' : 'border-primary-300'} ${multiline ? 'py-2 leading-snug' : 'h-9'}`;
    return (
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex items-center justify-between gap-2 text-xs text-grayscale-1100">
          <label htmlFor={`smp-${s.id}-${k}`}>
            {FIELD_LABEL[k]}
            {isOver(k) && <span className="text-amber-700"> · edited</span>}
            {changed.includes(k) && <span className="text-amber-700"> · unsaved</span>}
          </label>
          <span className="flex items-center gap-2">
            {(k === 'detail' || k === 'title') && (
              <span className={v.trim().length > LIMITS[k].max ? 'text-red-600' : ''}>{v.trim().length}/{LIMITS[k].max}</span>
            )}
            {isOver(k) && (
              <button type="button" onClick={() => resetField(k)} disabled={!!busy}
                title={`Back to: ${s.defaults[k] || '(empty)'}`}
                className="underline underline-offset-2 text-grayscale-1200 hover:text-grayscale-1300 disabled:opacity-50">
                {busy === `reset:${k}` ? 'Resetting…' : 'Reset to default'}
              </button>
            )}
          </span>
        </div>
        {multiline ? (
          <textarea id={`smp-${s.id}-${k}`} value={v} rows={3} placeholder={s.defaults[k]}
            onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} className={cls} />
        ) : (
          <input id={`smp-${s.id}-${k}`} value={v} placeholder={s.defaults[k]}
            onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} className={cls} />
        )}
        {isOver(k) && (
          <div className="text-[11px] text-grayscale-1100 truncate" title={s.defaults[k]}>
            Default: {s.defaults[k] || '(empty)'}
          </div>
        )}
        {p && <div className="text-[11px] text-red-600">{p}</div>}
      </div>
    );
  };

  // The file visitors receive (with the uploaded cover as page 1, when it carries one).
  const pdfSize = s.delivered_size_bytes || (s.pdf_size_bytes ?? s.size_bytes);
  const coverNote = s.pdf_cover ? PDF_COVER_NOTE[s.pdf_cover] : undefined;
  const stats: [string, any][] = [
    s.selections !== undefined ? ['Ticked', s.selections] : ['Links issued', s.links],
    ['Leads', s.leads], ['Downloads', s.downloads],
    ...(num(s.emailed) > 0 ? [['E-mailed', s.emailed] as [string, any]] : []),
  ];

  return (
    <div data-sample-id={s.id} className={`rounded-xl border bg-white flex flex-col ${s.active ? 'border-grayscale-300' : 'border-dashed border-grayscale-400'}`}>
      {/* ---- header: position, id, visibility ---- */}
      <div className="px-4 py-3 border-b border-grayscale-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <span className="text-[11px] rounded-full bg-grayscale-200 border border-grayscale-300 px-2 py-0.5 text-grayscale-1200"
            title={s.active ? 'Position on the page' : 'Not on the page'}>{s.active ? `#${position}` : 'hidden'}</span>
          <span className="text-sm font-bold text-grayscale-1300 font-grotesk truncate max-w-[36rem]" title={s.title}>{s.title}</span>
          <code className="text-[11px] text-grayscale-1100">{s.id}</code>
          {s.active ? <Pill tone="good">Shown on the page</Pill> : <Pill tone="neutral">Hidden from the page</Pill>}
          {(s.overridden.length > 0 || s.cover_uploaded || s.pdf_uploaded) && (
            <Pill tone="warn" title={[...s.overridden, s.cover_uploaded ? 'cover' : '', s.pdf_uploaded ? 'pdf' : '']
              .filter((x, i, a) => x && a.indexOf(x) === i).join(', ')}>edited</Pill>
          )}
        </div>
        <div className="flex items-center gap-2">
          {s.updated_at && (
            <span className="text-[11px] text-grayscale-1100">
              last edit {fmtDate(s.updated_at)}{s.updated_by ? ` by ${s.updated_by}` : ''}
            </span>
          )}
          {s.active
            ? <DangerBtn small onConfirm={() => post({ active: false }, 'Hidden — the page no longer offers this sample.', 'active')}>Hide from page</DangerBtn>
            : <Btn small onClick={() => post({ active: true }, 'Shown on the page again.', 'active')} disabled={!!busy}>Show on page</Btn>}
        </div>
      </div>

      <div className="p-4 grid gap-4 lg:grid-cols-[13rem_minmax(0,1fr)_16rem]">
        {/* ---- cover ---- */}
        <div className="flex flex-col gap-2">
          <div className="text-[10px] uppercase tracking-wide text-grayscale-1100">Cover image</div>
          <div className="aspect-[4/3] bg-grayscale-200 rounded-lg overflow-hidden border border-grayscale-300">
            {s.cover_url
              ? <img src={s.cover_url} alt={`Cover of ${s.title}`} className="w-full h-full object-cover object-top" loading="lazy" />
              : <div className="w-full h-full flex items-center justify-center text-xs text-grayscale-1100">no cover</div>}
          </div>
          <div className="text-[11px] text-grayscale-1100">
            {s.cover_uploaded
              ? <>Uploaded image{s.cover_updated_at ? ` · ${fmtDate(s.cover_updated_at)}` : ''}</>
              : 'Built-in image (from the deploy)'}
          </div>
          <Uploader kind="cover" sample={s} busy={busy} setBusy={setBusy}
            onDone={t => { setMsg({ kind: 'ok', text: t }); reload(); }}
            onError={t => setMsg(t ? { kind: 'error', text: t } : null)} />
          {s.cover_uploaded && (
            <DangerBtn small onConfirm={() => post({ reset: ['cover'] }, 'Cover is back to the built-in image.', 'reset:cover')}>
              Reset image to default
            </DangerBtn>
          )}
        </div>

        {/* ---- text ---- */}
        <div className="flex flex-col gap-3 min-w-0">
          {input('title')}
          {input('org')}
          {input('industry')}
          {input('detail', true)}
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_7rem] gap-3">
            {input('filename')}
            <div className="flex flex-col gap-1">
              <label htmlFor={`smp-${s.id}-sort`} className="text-xs text-grayscale-1100"
                title="Lower comes first on the page">
                Order{changed.includes('sort') && <span className="text-amber-700"> · unsaved</span>}
              </label>
              <input id={`smp-${s.id}-sort`} value={val('sort')} inputMode="numeric" placeholder="e.g. 10"
                onChange={e => setDraft(d => ({ ...d, sort: e.target.value }))}
                className={`h-9 w-full px-3 rounded-md border-[0.5px] bg-white text-sm ${sortBad ? 'border-red-400' : 'border-primary-300'}`} />
              {sortBad && <div className="text-[11px] text-red-600">A whole number 0–1000; lower comes first.</div>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Btn kind="primary" small onClick={save} disabled={!dirty || !!busy || problems.length > 0 || sortBad}>
              {busy === 'save' ? 'Saving…' : 'Save changes'}
            </Btn>
            {dirty && <Btn small onClick={() => setDraft({})} disabled={!!busy}>Discard</Btn>}
            {dirty && (
              <span className="text-[11px] text-amber-700">
                Unsaved: {changed.map(k => (k === 'sort' ? 'order' : FIELD_LABEL[k].toLowerCase())).join(', ')}
              </span>
            )}
          </div>
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        </div>

        {/* ---- PDF + stats ---- */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5 rounded-lg border border-grayscale-300 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] uppercase tracking-wide text-grayscale-1100">PDF people download</span>
              {s.pdf_uploaded ? <Pill tone="info">Uploaded</Pill> : <Pill tone="neutral">Built-in</Pill>}
            </div>
            <div className="text-xs text-grayscale-1300 break-all" title="The name it downloads under">{s.filename || '—'}</div>
            <div className="text-[11px] text-grayscale-1100">
              {[fmtBytes(pdfSize), s.pdf_uploaded && s.pdf_updated_at ? `uploaded ${fmtDate(s.pdf_updated_at)}` : '']
                .filter(Boolean).join(' · ') || '—'}
            </div>
            {coverNote && (
              <div className={`text-[11px] ${coverNote.warn ? 'text-amber-700' : 'text-grayscale-1100'}`}>{coverNote.text}</div>
            )}
            {s.file_present === false && (
              <div className="text-[11px] font-bold text-red-600">PDF missing on the server — downloads fail</div>
            )}
            <div className="flex flex-col gap-1.5 pt-1">
              {s.pdf_download_path && (
                <Btn small onClick={dlPdf} disabled={!!busy} title="The PDF exactly as visitors receive it">Download current</Btn>
              )}
              <Uploader kind="pdf" sample={s} busy={busy} setBusy={setBusy}
                onDone={t => { setMsg({ kind: 'ok', text: t }); reload(); }}
                onError={t => setMsg(t ? { kind: 'error', text: t } : null)} />
              {s.pdf_uploaded && (
                <DangerBtn small onConfirm={() => post({ reset: ['pdf'] }, 'The PDF is back to the built-in file.', 'reset:pdf')}>
                  Reset PDF to default
                </DangerBtn>
              )}
            </div>
            <div className="text-[10px] text-grayscale-1100">
              PDF only, up to {SAMPLE_PDF_MAX_MB} MB. Links already e-mailed get the new file too.
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <div className="grid grid-cols-3 gap-1 text-center">
              {stats.map(([l, v]) => (
                <div key={l} className="rounded-md bg-grayscale-100 border border-grayscale-200 py-1">
                  <div className="text-sm font-bold text-grayscale-1300">{v === undefined || v === null ? '—' : num(v).toLocaleString()}</div>
                  <div className="text-[10px] text-grayscale-1100">{l}</div>
                </div>
              ))}
            </div>
            <div className="bg-grayscale-200 rounded-full h-1.5 overflow-hidden" title="Downloads relative to the most-downloaded sample">
              <div className="h-1.5 bg-primary-500 rounded-full" style={{ width: `${(num(s.downloads) / maxDl) * 100}%` }} />
            </div>
            <div className="text-[10px] text-grayscale-1100 text-right">
              {s.last_downloaded_at ? `last downloaded ${fmtDate(s.last_downloaded_at)}` : 'never downloaded'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// How visitors receive samples (and how the paid report is sent)
// ---------------------------------------------------------------------------
type SampleMode = 'download' | 'email' | 'both';
type DeliveredMode = 'link' | 'attach' | 'both';
type DeliveryDraft = {
  mode: SampleMode; attach_max_mb: string; link_ttl_days: string; per_address_24h: string; global_per_hour: string;
  delivered_mode: DeliveredMode; delivered_max_mb: string;
};

const SAMPLE_MODES: { key: SampleMode; label: string; help: string }[] = [
  { key: 'download', label: 'Download on the page',
    help: 'The PDFs download as soon as the form is sent. Nothing is e-mailed — how it has always worked.' },
  { key: 'email', label: 'E-mail them',
    help: 'The PDFs are e-mailed as attachments and the page offers no download at all. Reports past the budget go as links.' },
  { key: 'both', label: 'Both',
    help: 'The PDFs download on the page and are e-mailed as well. A safe way to start.' },
];
const DELIVERED_MODES: { key: DeliveredMode; label: string; help: string }[] = [
  { key: 'link', label: 'Link only', help: 'The delivery e-mail carries the personal download link — as today.' },
  { key: 'attach', label: 'Attach the file', help: 'The report is attached when it fits the budget; the link stays as a backup.' },
  { key: 'both', label: 'Attach and link', help: 'The report is attached when it fits, and the e-mail invites them to use the link too.' },
];
const BOUNDS: Record<string, [number, number]> = {
  attach_max_mb: [1, 20], link_ttl_days: [1, 30], per_address_24h: [1, 20], global_per_hour: [1, 1000], delivered_max_mb: [1, 20],
};

const toDraft = (st: any): DeliveryDraft => {
  const sm = st?.samples || {}, dv = st?.delivered || {};
  const m = str(sm.mode);
  const dm = str(dv.mode);
  return {
    mode: (m === 'email' || m === 'both' ? m : 'download') as SampleMode,
    attach_max_mb: str(sm.attach_max_mb ?? 10), link_ttl_days: str(sm.link_ttl_days ?? 7),
    per_address_24h: str(sm.per_address_24h ?? 3), global_per_hour: str(sm.global_per_hour ?? 60),
    delivered_mode: (dm === 'attach' || dm === 'both' ? dm : 'link') as DeliveredMode,
    delivered_max_mb: str(dv.attach_max_mb ?? 10),
  };
};

const badNumber = (k: string, v: string): boolean => {
  const [lo, hi] = BOUNDS[k];
  return !/^\d+$/.test(v.trim()) || Number(v) < lo || Number(v) > hi;
};

const DeliveryCard: React.FC = () => {
  const res = useAdminGet(S, 'delivery_settings', {}, []);
  const d = res.data || {};
  const saved = toDraft(d.settings);
  const [draft, setDraft] = useState<DeliveryDraft | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Note>(null);
  const cur = draft || saved;
  const set = (k: keyof DeliveryDraft) => (v: string) => { setMsg(null); setDraft({ ...cur, [k]: v } as DeliveryDraft); };
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(saved);
  const numKeys: (keyof DeliveryDraft)[] = ['attach_max_mb', 'link_ttl_days', 'per_address_24h', 'global_per_hour', 'delivered_max_mb'];
  const invalid = numKeys.filter(k => badNumber(k, cur[k]));

  // Which active PDFs fit the (draft) budget, in page order — the server's rule.
  const pdfs: any[] = Array.isArray(d.samples) ? d.samples : [];
  const budget = (Number(cur.attach_max_mb) || 0) * 1048576;
  let used = 0;
  const plan = pdfs.map(p => {
    const size = num(p.size_bytes);
    const fits = !!p.file_present && size > 0 && used + size <= budget;
    if (fits) used += size;
    return { ...p, fits };
  });
  const total = num(d.samples_total_bytes) || pdfs.reduce((a, p) => a + num(p.size_bytes), 0);
  const linked = plan.filter(p => !p.fits);

  const save = async () => {
    if (!dirty || invalid.length) return;
    setBusy(true); setMsg(null);
    const { data, error } = await panelPost(S, 'delivery_settings_save', {
      samples: {
        mode: cur.mode, attach_max_mb: Number(cur.attach_max_mb), link_ttl_days: Number(cur.link_ttl_days),
        per_address_24h: Number(cur.per_address_24h), global_per_hour: Number(cur.global_per_hour),
      },
      delivered: { mode: cur.delivered_mode, attach_max_mb: Number(cur.delivered_max_mb) },
    });
    setBusy(false);
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setDraft(null);
    setMsg({ kind: 'ok', text: data?.changed === false ? 'Nothing changed.' : 'Saved. The page uses it on its next load.' });
    res.reload();
  };

  const numField = (k: keyof DeliveryDraft, label: string, unit: string) => (
    <label className="flex flex-col gap-1 text-xs text-grayscale-1200">
      {label}
      <span className="flex items-center gap-1.5">
        <input value={cur[k]} inputMode="numeric" onChange={e => set(k)(e.target.value)} disabled={busy}
          className={`h-9 w-20 px-2 rounded-md border-[0.5px] bg-white text-sm ${invalid.includes(k) ? 'border-red-400' : 'border-primary-300'}`} />
        <span className="text-grayscale-1100">{unit}</span>
      </span>
      {invalid.includes(k) && <span className="text-[11px] text-red-600">{BOUNDS[k][0]}–{BOUNDS[k][1]}</span>}
    </label>
  );

  return (
    <Section title="How visitors receive samples"
      right={
        <AdminLink to={{ group: 'ip_intel', tab: 'emails' }} className="text-xs text-primary-700 underline">
          Edit the e-mail (Emails tab)
        </AdminLink>
      }>
      {res.error && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading /> : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="How visitors receive samples">
            {SAMPLE_MODES.map(m => (
              <label key={m.key} className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer ${cur.mode === m.key ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300'}`}>
                <input type="radio" name="ipi-sample-mode" checked={cur.mode === m.key} onChange={() => set('mode')(m.key)}
                  disabled={busy} className="mt-1" />
                <span className="flex flex-col">
                  <span className="text-sm font-bold text-grayscale-1300">
                    {m.label}{saved.mode === m.key && <span className="ml-2 text-[11px] font-normal text-grayscale-1100">(live now)</span>}
                  </span>
                  <span className="text-xs text-grayscale-1100">{m.help}</span>
                </span>
              </label>
            ))}
          </div>
          {cur.mode !== 'download' && saved.mode === 'download' && (
            <Msg kind="error">
              Every sample request will send a real e-mail of up to {cur.attach_max_mb || '?'} MB from inbox@universitytech.io.
              Send yourself a test from the Emails tab (“IP Intel — sample reports”) before you save.
            </Msg>
          )}
          <div className="flex flex-wrap gap-4">
            {numField('attach_max_mb', 'Attachment budget', 'MB')}
            {numField('link_ttl_days', 'E-mailed links work for', 'days')}
            {numField('per_address_24h', 'Mails per address', 'a day')}
            {numField('global_per_hour', 'Mails in total', 'an hour')}
          </div>
          <div className="flex flex-col gap-1.5 rounded-lg border border-grayscale-300 p-3">
            <div className="flex items-center justify-between gap-2 text-xs text-grayscale-1200 flex-wrap">
              <span>Active sample PDFs: <strong>{fmtBytes(total) || '0 KB'}</strong> of a {cur.attach_max_mb || '?'} MB budget</span>
              {linked.length > 0
                ? <Pill tone="warn">{linked.length} sent as a link</Pill>
                : pdfs.length > 0 && <Pill tone="good">All attached</Pill>}
            </div>
            <div className="bg-grayscale-200 rounded-full h-1.5 overflow-hidden">
              <div className={`h-1.5 rounded-full ${total > budget ? 'bg-amber-500' : 'bg-primary-500'}`}
                style={{ width: `${Math.min(100, budget ? (total / budget) * 100 : 100)}%` }} />
            </div>
            <ul className="text-[11px] text-grayscale-1100 flex flex-col gap-0.5">
              {plan.map(p => (
                <li key={p.id} className="flex justify-between gap-2">
                  <span className="truncate" title={str(p.title)}>{str(p.title) || p.id}</span>
                  <span className="whitespace-nowrap">
                    {p.file_present === false ? 'PDF missing' : `${fmtBytes(p.size_bytes)} · ${p.fits ? 'attached' : 'link'}`}
                  </span>
                </li>
              ))}
            </ul>
            <div className="text-[10px] text-grayscale-1100">
              Gmail and Outlook refuse mail over about 20–25 MB, and many company servers stop at 10 MB.
            </div>
          </div>
          <div className="flex flex-col gap-2 border-t border-grayscale-200 pt-3">
            <div className="text-xs font-bold text-grayscale-1300">Paid report (the delivery e-mail)</div>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="How the paid report is sent">
              {DELIVERED_MODES.map(m => (
                <label key={m.key} title={m.help}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs cursor-pointer ${cur.delivered_mode === m.key ? 'border-primary-500 bg-primary-50 font-bold' : 'border-grayscale-300'}`}>
                  <input type="radio" name="ipi-delivered-mode" checked={cur.delivered_mode === m.key}
                    onChange={() => set('delivered_mode')(m.key)} disabled={busy} />
                  {m.label}
                </label>
              ))}
            </div>
            <div className="text-[11px] text-grayscale-1100">{DELIVERED_MODES.find(m => m.key === cur.delivered_mode)?.help}</div>
            {cur.delivered_mode !== 'link' && numField('delivered_max_mb', 'Attach reports up to', 'MB')}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Btn kind="primary" small onClick={save} disabled={!dirty || busy || invalid.length > 0}>
              {busy ? 'Saving…' : 'Save'}
            </Btn>
            {dirty && <Btn small onClick={() => { setDraft(null); setMsg(null); }} disabled={busy}>Discard</Btn>}
            {dirty && <span className="text-[11px] text-amber-700">Unsaved changes</span>}
          </div>
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        </div>
      )}
    </Section>
  );
};

const Samples: React.FC<{ samples: Sample[]; loading: boolean; error: string; reload: () => void }> =
  ({ samples, loading, error, reload }) => {
  const maxDl = Math.max(1, ...samples.map(s => num(s.downloads)));
  // Page order: active first by sort, then the server's order; hidden ones last.
  const ordered = samples.map((s, i) => ({ s, i }))
    .sort((a, b) => (Number(b.s.active) - Number(a.s.active))
      || ((a.s.sort ?? a.i) - (b.s.sort ?? b.i)) || (a.i - b.i))
    .map(x => x.s);
  let shownPos = 0;
  return (
    <div className="flex flex-col gap-4">
    <DeliveryCard />
    <Section title="Sample reports"
      right={<Btn small onClick={reload} disabled={loading}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        What you change here is what <a href="/ip-intel-report#samples" target="_blank" rel="noopener noreferrer"
          className="text-primary-700 underline">the page</a> shows on its next load — the card text, the cover, the
        PDF people download and the name it downloads under. Each edit overrides the built-in copy for that one field;
        “Reset to default” brings the built-in version back. Uploaded files are kept on the server.
      </p>
      {error && <Msg kind="error">{error}</Msg>}
      {loading && !samples.length ? <Loading /> : samples.length === 0 ? (
        <div className="text-sm text-grayscale-1100 py-4 text-center">No sample reports configured.</div>
      ) : (
        <div className="flex flex-col gap-3">
          {ordered.map(s => {
            if (s.active) shownPos += 1;
            return <SampleCard key={s.id} s={s} position={s.active ? shownPos : 0} maxDl={maxDl} reload={reload} />;
          })}
        </div>
      )}
    </Section>
    </div>
  );
};

export default Samples;
